import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TagBloc } from '@kbloc';
import { AuthenticationBloc } from '@kp/auth/authentication.bloc';
import { UserBloc } from '@kp/user/user.bloc';
import { DataStoreService, OnboardingUtilities, TitleService } from '@kservice';
import { UserType } from '@ktypes/enums';
import { Status } from '@ktypes/models';
import { validateKeyIsNumber } from '@kutil';
import { of, skipWhile, Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'kp-screen-verification-email',
  templateUrl: './screen-verification-email.component.html',
  styleUrls: ['./screen-verification-email.component.scss'],
})
export class ScreenVerificationEmailComponent implements OnInit, OnDestroy {
  private _codeControl: UntypedFormControl;
  private _destroy$ = new Subject<void>();
  private _title = 'Email Verification Code';
  private _userSubmitted = false;

  email: string;
  errorMessage: string;
  form: UntypedFormGroup;
  formatError = false;
  verifyError = false;

  constructor(
    private _authenticationBloc: AuthenticationBloc,
    private _dataStore: DataStoreService,
    private _onboardingUtilities: OnboardingUtilities,
    private _router: Router,
    private _tagBloc: TagBloc,
    private _titleService: TitleService,
    private _userBloc: UserBloc
  ) {}

  ngOnInit(): void {
    this._titleService.set(this._title);
    this._codeControl = new UntypedFormControl('', Validators.pattern(/^\d{6}$/));
    this.form = new UntypedFormGroup({
      code: this._codeControl,
    });
    this._codeControl.valueChanges.pipe(takeUntil(this._destroy$)).subscribe((value) => {
      this.formatError =
        value == null ||
        (value?.length > 5 && (this._codeControl.touched || this._codeControl.dirty) && this._codeControl.invalid);
    });

    this._userBloc.registerStatus$
      .pipe(
        // only enable this after user submits
        skipWhile(() => !this._userSubmitted),
        switchMap((registerStatus) => {
          if (registerStatus?.status === Status.error && !registerStatus?.data) {
            // possible errors:
            // - bad request,
            // - KumanuIdentityServiceError - CodeMismatchException,
            // - KumanuIdentityServiceError - ExpiredCodeException,
            // - KumanuIdentityServiceError - AliasExistsException
            this.errorMessage = this._authenticationBloc.getAuthErrorMessage(
              registerStatus?.message?.message,
              'The code you entered is invalid.'
            );
            this.verifyError = true;
            return of(null);
          }
          return this._dataStore.user$;
        }),
        takeUntil(this._destroy$)
      )
      .subscribe((user) => {
        if (user?.type === UserType.user) {
          this._tagBloc.getUserTags();

          // move on to set password
          void this._router.navigate(['/welcome/signup/password']);
        }

        // reset userSubmitted
        if (this._userSubmitted) {
          this._userSubmitted = false;
        }
      });
  }

  ngOnDestroy() {
    this._destroy$.next();
    this._destroy$.complete();
  }

  getUserEmail(email: string) {
    this.email = email;
  }

  codeChanged() {
    // reset error state if they are "true"
    if (this.verifyError) {
      this.verifyError = false;
    }
  }

  onSubmit(value) {
    if (this._userSubmitted) {
      // only allow the first one through
      return;
    }
    this._userSubmitted = true;
    if (this.form?.valid) {
      // clear any existing Payment Required Error Messages, then attempt registration
      this._authenticationBloc.resetPaymentRequiredErrorMessage();
      this._userBloc.registerUser(this.email, value.code);
    }
  }

  handleKeydown($event: KeyboardEvent) {
    // reset error state if they are "true"
    if (this.verifyError) {
      this.verifyError = false;
    }
    if (!validateKeyIsNumber($event)) {
      $event.preventDefault();
      return false;
    }
  }
}
